import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  DialogActions,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import TablePagination from "@mui/material/TablePagination";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector, useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { useToken } from "../../../../hooks/useToken";
import { fetchCategories } from "../../../../actions/categories";
import { client } from "../../../../axios/client";
import {
  listContactsCatalog,
  searchContactsByNameCatalog,
} from "../../../../actions/campaigns/get-contacts.actions";

export const ContactsModal = ({
  open,
  onClose,
  initialSelected = [],
  onConfirm,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedContactsCache, setSelectedContactsCache] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const searchTimerRef = useRef(null);

  const { id_customer } = useSelector((state) => state.agent);
  const { instance } = useMsal();
  const { tokenRequest } = useToken();
  const dispatch = useDispatch();

  const categoriesData = useSelector((state) => state.categories.items);
  const categoriesStatus = useSelector((state) => state.categories.status);
  const categoryList = categoriesData?.items || [];
  const loadingCategories = categoriesStatus === "loading";

  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedCategoryFilter([]);
      loadPage(0, rowsPerPage, "");
      setSelectedContacts(initialSelected);
      setSelectedContactsCache(new Map());
      if (categoriesStatus === "idle") {
        instance
          .acquireTokenSilent(tokenRequest)
          .then(({ accessToken }) =>
            dispatch(fetchCategories(id_customer, accessToken, 0, 100))
          )
          .catch(console.error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const normalizeItems = useCallback(
    (items) =>
      (items || []).map((i) => ({
        id: i._id,
        nombre:
          typeof i.first_name === "string" && i.first_name.trim().length > 0
            ? i.first_name.trim()
            : "Sin nombre",
        telefono: i.phone || "",
        categorias: Array.isArray(i.categories)
          ? i.categories.map((c) => c?.name).filter(Boolean)
          : [],
      })),
    []
  );

  const loadPage = useCallback(
    async (pageIndex, limit, term) => {
      setLoading(true);
      try {
        const { accessToken } = await instance.acquireTokenSilent(tokenRequest);
        const offset = pageIndex * limit;

        const result = term
          ? await searchContactsByNameCatalog({
              firstName: term,
              idCustomer: id_customer,
              token: accessToken,
              offset,
              limit,
            })
          : await listContactsCatalog({
              idCustomer: id_customer,
              token: accessToken,
              offset,
              limit,
            });

        setContacts(normalizeItems(result.items));
        setCount(result.count || 0);
        setPage(pageIndex);
        setRowsPerPage(limit);
      } catch (error) {
        console.error("Error al cargar contactos:", error);
        setContacts([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    },
    [id_customer, instance, tokenRequest, normalizeItems]
  );

  useEffect(() => {
    if (!open) return;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (selectedCategoryFilter.length > 0) return;

    searchTimerRef.current = setTimeout(() => {
      loadPage(0, rowsPerPage, searchTerm?.trim());
    }, 500);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchTerm, open, loadPage, rowsPerPage, selectedCategoryFilter]);

  useEffect(() => {
    if (!open) return;

    if (selectedCategoryFilter.length === 0) {
      loadPage(0, rowsPerPage, searchTerm?.trim());
      return;
    }

    const loadByCategories = async () => {
      setLoading(true);
      try {
        const { accessToken } = await instance.acquireTokenSilent(tokenRequest);
        const ids = selectedCategoryFilter.map((c) => c.name);
        const response = await client.get(`/contacts/categories/${id_customer}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { categories: ids },
          paramsSerializer: (params) =>
            Object.keys(params)
              .map((key) =>
                params[key].map((v) => `${key}=${encodeURIComponent(v)}`).join("&")
              )
              .join("&"),
        });
        setContacts(normalizeItems(response.data.items || []));
        setCount(response.data.count || 0);
        setPage(0);
      } catch (error) {
        console.error("Error al filtrar por categorías:", error);
        setContacts([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadByCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryFilter, open]);

  const filteredContacts = contacts;  //* De momento no filtra pero es un punto de extensión para filtros cliente-side futurs

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const currentPageIds = filteredContacts.map((contact) => contact.id);

      setSelectedContacts((prev) => {
        const combined = [...new Set([...prev, ...currentPageIds])];
        return combined;
      });

      setSelectedContactsCache((cache) => {
        const newCache = new Map(cache);
        filteredContacts.forEach((contact) => {
          newCache.set(contact.id, {
            id: contact.id,
            nombre: contact.nombre,
            telefono: contact.telefono,
          });
        });
        return newCache;
      });
    } else {
      // Deseleccionar solo los de la página actual
      const currentPageIds = filteredContacts.map((contact) => contact.id);

      setSelectedContacts((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );

      // Quitar del caché solo los de esta página
      setSelectedContactsCache((cache) => {
        const newCache = new Map(cache);
        currentPageIds.forEach((id) => newCache.delete(id));
        return newCache;
      });
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts((prev) => {
      if (prev.includes(contactId)) {
        setSelectedContactsCache((cache) => {
          const newCache = new Map(cache);
          newCache.delete(contactId);
          return newCache;
        });
        return prev.filter((id) => id !== contactId);
      } else {
        const contact = contacts.find((c) => c.id === contactId);
        if (contact) {
          setSelectedContactsCache((cache) => {
            const newCache = new Map(cache);
            newCache.set(contactId, {
              id: contact.id,
              nombre: contact.nombre,
              telefono: contact.telefono,
            });
            return newCache;
          });
        }
        return [...prev, contactId];
      }
    });
  };

  const handleConfirm = () => {
    const selectedContactsData = Array.from(selectedContactsCache.values());
    onConfirm(selectedContactsData);
    onClose();
  };

  const handleCancel = () => {
    setSelectedContacts(initialSelected);
    setSelectedContactsCache(new Map());
    onClose();
  };

  const isAllSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every((contact) => selectedContacts.includes(contact.id));

  const isSomeSelected = selectedContacts.length > 0 && !isAllSelected;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "500px",
          mt: 11,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
          <Typography variant="h3" sx={{ py:2 }}>Contactos</Typography>

        <IconButton
          aria-label="cerrar"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
            "&:hover": {
              color: (theme) => theme.palette.grey[700],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{}}>
        <Box sx={{ mb: 1, display: "flex", gap: 1.5 }}>
          <TextField
            sx={{ width: "50%" }}
            placeholder="Buscar contacto"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={selectedCategoryFilter.length > 0}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            multiple
            sx={{ width: "50%" }}
            options={categoryList}
            getOptionLabel={(option) => option.name || ""}
            value={selectedCategoryFilter}
            onChange={(_, newValue) => setSelectedCategoryFilter(newValue)}
            loading={loadingCategories}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={option.id}
                  label={option.name}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filtrar por categoría"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCategories ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <TableContainer sx={{ maxHeight: 350 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Nombre de contacto</TableCell>
                <TableCell>Categorías</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Cargando contactos...
                  </TableCell>
                </TableRow>
              ) : filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron contactos
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    onClick={() => handleSelectContact(contact.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>{contact.telefono}</TableCell>
                    <TableCell>{contact.nombre}</TableCell>
                    <TableCell>
                      {contact.categorias && contact.categorias.length > 0 ? (
                        contact.categorias.map((cat, idx) => (
                          <Chip
                            key={idx}
                            label={cat}
                            color="primary"
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))
                      ) : (
                        <Chip
                          label="Sin categoría"
                          color="primary"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {selectedContacts.length > 0 &&
              `${selectedContactsCache.size} contacto(s) seleccionado(s) de ${count} total`}
          </Box>

          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(e, newPage) => {
              loadPage(newPage, rowsPerPage, searchTerm?.trim());
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newLimit = parseInt(e.target.value, 10);
              loadPage(0, newLimit, searchTerm?.trim());
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedContacts.length === 0}
        >
          Seleccionar
        </Button>
        <Button variant="outlined" onClick={handleCancel}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
